import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pause, Play } from "lucide-react";

// Slideshow-style automatic tour of the invitation.
// After clicking "Begin the Journey" the site smoothly glides from one section
// to the next, dwelling on each for a moment so guests can read comfortably.
const SLIDE_SECTIONS = [
    "invitation",
    "timeline",
    "shukrana",
    "nikah",
    "valima",
    "footer",
];
const DWELL_MS = 5500; // time to read each section
const TRANSITION_MS = 1400; // approximate smooth-scroll duration

export const AutoScroll = () => {
    const [visible, setVisible] = useState(false);
    const [playing, setPlaying] = useState(false);
    const indexRef = useRef(0);
    const timeoutRef = useRef(null);
    const programmaticRef = useRef(false);

    const clearTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const stop = useCallback(() => {
        clearTimer();
        setPlaying(false);
    }, []);

    const gotoIndex = useCallback((i) => {
        const id = SLIDE_SECTIONS[i];
        const el = document.getElementById(id);
        if (!el) return false;
        programmaticRef.current = true;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // release the "programmatic scroll" flag once the animation completes
        setTimeout(() => {
            programmaticRef.current = false;
        }, TRANSITION_MS + 200);
        return true;
    }, []);

    const scheduleNext = useCallback(() => {
        clearTimer();
        timeoutRef.current = setTimeout(() => {
            const nextIndex = indexRef.current + 1;
            if (nextIndex >= SLIDE_SECTIONS.length) {
                stop();
                return;
            }
            indexRef.current = nextIndex;
            gotoIndex(nextIndex);
            scheduleNext();
        }, DWELL_MS + TRANSITION_MS);
    }, [gotoIndex, stop]);

    const start = useCallback(() => {
        clearTimer();
        // Find the section currently closest to the viewport top and resume from there
        const currentY = window.scrollY;
        let bestIndex = 0;
        let bestDelta = Infinity;
        SLIDE_SECTIONS.forEach((id, i) => {
            const el = document.getElementById(id);
            if (!el) return;
            const top = el.getBoundingClientRect().top + currentY;
            const delta = Math.abs(top - currentY);
            if (delta < bestDelta) {
                bestDelta = delta;
                bestIndex = i;
            }
        });
        indexRef.current = bestIndex;
        setPlaying(true);
        gotoIndex(bestIndex);
        scheduleNext();
    }, [gotoIndex, scheduleNext]);

    // Show floating control once past the hero
    useEffect(() => {
        const onScroll = () => {
            if (window.scrollY > window.innerHeight * 0.6) setVisible(true);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // External kickoff from Hero's "Begin the Journey"
    useEffect(() => {
        const handler = () => {
            setVisible(true);
            // wait a tick so the initial scroll into #invitation settles first
            setTimeout(() => start(), 1200);
        };
        window.addEventListener("autoscroll:start", handler);
        return () => window.removeEventListener("autoscroll:start", handler);
    }, [start]);

    // Pause when the user grabs control
    useEffect(() => {
        const pauseOnUser = () => {
            if (!playing) return;
            if (programmaticRef.current) return;
            stop();
        };
        const events = ["wheel", "touchstart", "keydown"];
        events.forEach((ev) =>
            window.addEventListener(ev, pauseOnUser, { passive: true }),
        );
        return () => {
            events.forEach((ev) =>
                window.removeEventListener(ev, pauseOnUser),
            );
        };
    }, [playing, stop]);

    // Pause when tab hidden
    useEffect(() => {
        const onVis = () => {
            if (document.hidden && playing) stop();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [playing, stop]);

    useEffect(() => () => clearTimer(), []);

    const toggle = () => {
        if (playing) stop();
        else start();
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.button
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                    onClick={toggle}
                    data-testid="autoscroll-toggle"
                    aria-label={playing ? "Pause slideshow" : "Play slideshow"}
                    className="fixed z-40 bottom-6 right-6 md:bottom-8 md:right-8 h-11 w-11 md:h-12 md:w-12 rounded-full flex items-center justify-center backdrop-blur-md transition-colors duration-500"
                    style={{
                        backgroundColor: playing
                            ? "rgba(212, 181, 122, 0.9)"
                            : "rgba(20, 17, 12, 0.55)",
                        border: "1px solid rgba(212, 181, 122, 0.55)",
                        color: playing ? "#14110c" : "#ecd6a1",
                    }}
                >
                    {playing ? (
                        <Pause size={14} strokeWidth={1.5} />
                    ) : (
                        <Play
                            size={14}
                            strokeWidth={1.5}
                            style={{ marginLeft: 2 }}
                        />
                    )}
                </motion.button>
            )}
        </AnimatePresence>
    );
};

export default AutoScroll;
